import { css } from 'lit';

/**
 * What a card does when the page is printed (Phase 37).
 *
 * This is the whole of the image export. The Development Plan asked for PNG and PDF render jobs;
 * they were dropped at Donovan's direction on 2026-09-26 in favour of the browser's own print,
 * which has three advantages a server-side renderer would have had to work to match: it already
 * has the webfonts loaded, it already has the chart laid out at the width the reader chose, and
 * the confidence patterns (PRD 7.3, Appendix A) are already vector `<pattern>` fills, so they
 * survive a print and a greyscale print without anybody converting anything.
 *
 * What the rules below have to do is get out of the way:
 *
 *   - **Print the ink.** Browsers drop background colours and background images by default, which
 *     would take every band colour and every hatch and dot fill with them — the one thing the
 *     encoding cannot lose. `print-color-adjust: exact` is inherited, so the shell sets it on
 *     `body` and it reaches in here; it is repeated on the host because Safari has historically
 *     honoured it only where it is declared.
 *   - **Keep a card whole.** A chart split down the middle of a page is not a chart.
 *   - **Show the table.** PRD 7.3: every chart has a table, and the table is what a reader
 *     checks a number against. On screen it is a closed `<details>`; on paper there is nothing
 *     to click, so it prints open, after the chart.
 *   - **Drop the controls.** Buttons, pickers and the download link are furniture; they should
 *     not use up a printed page.
 *
 * A component adds this to its own styles — `static styles = [PRINT_STYLES, css\`…\`]` — because
 * a page-level stylesheet cannot reach into a shadow root. Custom properties do pierce the
 * boundary, which is how the shell forces the light palette for print.
 */
export const PRINT_STYLES = css`
  @media print {
    :host {
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
      break-inside: avoid;
    }
    .card {
      box-shadow: none;
      border-color: #999;
      break-inside: avoid;
    }
    /* Furniture: nothing here can be operated on paper. */
    button,
    select,
    input,
    .controls,
    .actions,
    .toolbar,
    .pager,
    .sort,
    .no-print {
      display: none !important;
    }
    /* PRD 7.3: the table is what a number is checked against, so it prints with its chart. */
    details.table,
    details {
      display: block;
      break-inside: auto;
    }
    details > summary {
      display: none;
    }
    details > *:not(summary) {
      display: revert;
    }
    table {
      break-inside: auto;
      font-size: 10px;
    }
    thead {
      display: table-header-group;
    }
    tr {
      break-inside: avoid;
    }
  }
`;
